import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import bannerImage from "./assets/kwonBanner.png";




const holes = Array.from({ length: 18 }, (_, i) => i + 1);
const customCourseName = "Custom Course";
const STORAGE_KEY = "kwon-skin-calculator-state";

const starterPlayers = [
  { name: "TH", handicap: 10.2 },
  { name: "Ty", handicap: 14.6 },
  { name: "Bill", handicap: 15 },
  { name: "Lou", handicap: 5.5 },
];

const courses = [
  {
    name: "Tubac Rancho/Otero",
    par: [4, 4, 4, 5, 3, 4, 3, 5, 4, 4, 5, 3, 4, 4, 4, 5, 3, 4],
    handicap: [17, 9, 5, 3, 15, 7, 11, 1, 13, 18, 12, 10, 2, 4, 14, 16, 6, 8],
  },
  {
    name: "Tubac Anza/Rancho",
    par: [4, 5, 4, 3, 5, 4, 3, 4, 3, 4, 4, 4, 5, 3, 4, 3, 5, 4],
    handicap: [7, 11, 1, 9, 15, 5, 13, 3, 17, 18, 10, 6, 4, 16, 8, 12, 2, 14],
  },
  {
    name: "Tubac Otero/Anza",
    par: [4, 5, 3, 4, 4, 4, 5, 3, 4, 4, 5, 4, 3, 5, 4, 3, 4, 3],
    handicap: [17, 11, 9, 1, 3, 13, 15, 5, 7, 8, 12, 2, 10, 16, 6, 14, 4, 18],
  },
  {
    name: "Tubac Otero/Rancho",
    par: [4, 5, 3, 4, 4, 4, 5, 3, 4, 4, 4, 4, 5, 3, 4, 3, 5, 4],
    handicap: [17, 11, 9, 1, 3, 13, 15, 5, 7, 18, 10, 6, 4, 16, 8, 12, 2, 14],
  },
  {
    name: "Tubac Anza/Otero",
    par: [4, 5, 4, 3, 5, 4, 3, 4, 3, 4, 5, 3, 4, 4, 4, 5, 3, 4],
    handicap: [7, 11, 1, 9, 15, 5, 13, 3, 17, 18, 12, 10, 2, 4, 14, 16, 6, 8],
  },
  {
    name: "Canoa Ranch Golf Club",
    par: [4, 5, 3, 5, 3, 4, 4, 3, 5, 4, 5, 4, 3, 4, 3, 4, 3, 5],
    handicap: [8, 4, 16, 10, 12, 6, 14, 18, 2, 9, 3, 5, 11, 7, 15, 13, 17, 1],
  },
];

const blankScores = starterPlayers.map(() => Array(18).fill(""));

function getDefaultState() {
  return {
    players: starterPlayers,
    selectedCourse: courses[0].name,
    par: courses[0].par,
    holeHandicap: courses[0].handicap,
    scores: blankScores,
  };
}

function getSavedState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return getDefaultState();

    const parsed = JSON.parse(saved);

    return {
      players: parsed.players || starterPlayers,
      selectedCourse: parsed.selectedCourse || courses[0].name,
      par: parsed.par || courses[0].par,
      holeHandicap: parsed.holeHandicap || courses[0].handicap,
      scores: parsed.scores || blankScores,
    };
  } catch {
    return getDefaultState();
  }
}

function asNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function roundToTenth(value) {
  return Math.round(asNumber(value) * 10) / 10;
}

function App() {
  const savedState = getSavedState();

  const [players, setPlayers] = useState(savedState.players);
  const [selectedCourse, setSelectedCourse] = useState(savedState.selectedCourse);
  const [par, setPar] = useState(savedState.par);
  const [holeHandicap, setHoleHandicap] = useState(savedState.holeHandicap);
  const [scores, setScores] = useState(savedState.scores);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        players,
        selectedCourse,
        par,
        holeHandicap,
        scores,
      })
    );
  }, [players, selectedCourse, par, holeHandicap, scores]);

  const results = useMemo(() => {
    const handicaps = players.map((p) => asNumber(p.handicap));
    const minHandicap = Math.min(...handicaps);
    const strokesOff = handicaps.map((h) => roundToTenth(h - minHandicap));

    const strokes = players.map((_, playerIndex) =>
      holes.map((_, holeIndex) =>
        asNumber(holeHandicap[holeIndex]) - strokesOff[playerIndex] <= 0 ? 1 : 0
      )
    );

    const adjusted = players.map((_, playerIndex) =>
      holes.map((_, holeIndex) => {
        const score = scores[playerIndex][holeIndex];
        if (score === "" || score === null || score === undefined) return "";
        return asNumber(score) - strokes[playerIndex][holeIndex];
      })
    );

    const winners = players.map(() => Array(18).fill(0));

    holes.forEach((_, holeIndex) => {
      const holeScores = adjusted.map((row) => row[holeIndex]).filter((v) => v !== "");
      if (holeScores.length !== players.length) return;

      const minScore = Math.min(...holeScores);
      const minCount = holeScores.filter((v) => v === minScore).length;
      if (minCount !== 1) return;

      const winnerIndex = adjusted.findIndex((row) => row[holeIndex] === minScore);
      winners[winnerIndex][holeIndex] = 1;
    });

    const hasWinner = holes.map((_, holeIndex) =>
      winners.reduce((sum, row) => sum + row[holeIndex], 0)
    );

    const holeValues = holes.map((hole, holeIndex) => {
      if (hasWinner[holeIndex] !== 1) return 0;

      let startIndex = 0;
      for (let i = holeIndex - 1; i >= 0; i -= 1) {
        if (hasWinner[i] === 1) {
          startIndex = i + 1;
          break;
        }
      }

      return holes.slice(startIndex, holeIndex + 1).reduce((sum, h) => sum + h, 0);
    });

    const winnerValues = players.map((_, playerIndex) =>
      holes.map((_, holeIndex) =>
        winners[playerIndex][holeIndex] === 1 ? holeValues[holeIndex] : ""
      )
    );

    const totalEarned = winnerValues.map((row) =>
      row.reduce((sum, value) => sum + asNumber(value), 0)
    );

    const total = totalEarned.reduce((sum, value) => sum + value, 0);
    const perPerson = total / players.length;
    const profitLoss = totalEarned.map((value) => value - perPerson);

    return {
      strokesOff,
      strokes,
      adjusted,
      winners,
      hasWinner,
      holeValues,
      winnerValues,
      totalEarned,
      total,
      perPerson,
      profitLoss,
    };
  }, [players, holeHandicap, scores]);

  const updatePlayer = (index, field, value) => {
    setPlayers((current) =>
      current.map((player, i) =>
        i === index ? { ...player, [field]: value } : player
      )
    );
  };

  const updateArrayValue = (setter, index, value) => {
    setter((current) => current.map((item, i) => (i === index ? value : item)));
  };

  const updateScore = (playerIndex, holeIndex, value) => {
    setScores((current) =>
      current.map((row, r) =>
        r === playerIndex
          ? row.map((cell, c) => (c === holeIndex ? value : cell))
          : row
      )
    );
  };

  const resetScores = () => {
    setScores(players.map(() => Array(18).fill("")));
  };


  const changeCourse = (courseName) => {
    if (courseName === customCourseName) {
      setSelectedCourse(customCourseName);
      setPar(Array(18).fill(""));
      setHoleHandicap(Array(18).fill(""));
      return;
    }

    const course = courses.find((item) => item.name === courseName);
    if (!course) return;

    setSelectedCourse(course.name);
    setPar(course.par);
    setHoleHandicap(course.handicap);
  };

  const updateCustomPar = (index, value) => {
    setSelectedCourse(customCourseName);
    updateArrayValue(setPar, index, value);
  };

  const updateCustomHoleHandicap = (index, value) => {
    setSelectedCourse(customCourseName);
    updateArrayValue(setHoleHandicap, index, value);
  };

  const formatNumber = (value) => {
    const rounded = Math.round(asNumber(value) * 100) / 100;
    return rounded === 0 ? "0" : rounded.toString();
  };

  const formatTenth = (value) => {
    return roundToTenth(value).toFixed(1);
  };

  return (
    <div className="app-page">
      <div className="app-container">
  
        <div className="banner-container">
          <img
            src={bannerImage}
            alt="Kwon Banner"
            className="banner-image"
          />
        </div>
  
        <div className="app-header">
          <div>
            <h1>Kwon Skin Calculator</h1>
          </div>

          <div className="header-actions">
            <select
              className="select"
              value={selectedCourse}
              onChange={(e) => changeCourse(e.target.value)}
            >
              {courses.map((course) => (
                <option key={course.name} value={course.name}>
                  {course.name}
                </option>
              ))}
              <option value={customCourseName}>{customCourseName}</option>
            </select>

            <button className="button" onClick={resetScores}>
              Clear Scores
            </button>

          </div>
        </div>

        <div className="player-grid">
          {players.map((player, index) => (
            <div className="card" key={index}>
              <div className="small-label">Player {index + 1}</div>
              <input
                className="input player-name"
                value={player.name}
                onChange={(e) => updatePlayer(index, "name", e.target.value)}
              />

              <div className="two-column">
                <label>
                  Handicap
                  <input
                    className="input"
                    type="text"
                    inputMode="decimal"
                    value={player.handicap}
                    onChange={(e) => updatePlayer(index, "handicap", e.target.value)}
                  />
                </label>

                <label>
                  Strokes Off
                  <div className="readonly-box">{formatTenth(results.strokesOff[index])}</div>
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="section-title-row">
            <h2>Score Entry</h2>
            <span>
              {selectedCourse === customCourseName
                ? "Custom course selected. Enter par and handicap by hole."
                : "Enter gross scores by hole."}
            </span>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Hole</th>
                  {holes.map((hole) => <th key={hole}>{hole}</th>)}
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td className="row-label">Par</td>
                  {par.map((value, index) => (
                    <td key={index}>
                      <input
                        className="table-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={value}
                        onChange={(e) => updateCustomPar(index, e.target.value)}
                      />
                    </td>
                  ))}
                </tr>

                <tr>
                  <td className="row-label">Handicap</td>
                  {holeHandicap.map((value, index) => (
                    <td key={index}>
                      <input
                        className="table-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={value}
                        onChange={(e) => updateCustomHoleHandicap(index, e.target.value)}
                      />
                    </td>
                  ))}
                </tr>

                {players.map((player, playerIndex) => (
                  <tr key={playerIndex}>
                    <td className="row-label">{player.name}</td>
                    {holes.map((_, holeIndex) => (
                      <td key={holeIndex}>
                        <input
                          className="table-input"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={scores[playerIndex][holeIndex]}
                          onChange={(e) => updateScore(playerIndex, holeIndex, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h2>Hole Results</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Metric</th>
                  {holes.map((hole) => <th key={hole}>{hole}</th>)}
                </tr>
              </thead>

              <tbody>
                {players.map((player, playerIndex) => (
                  <tr key={`adj-${playerIndex}`}>
                    <td className="row-label">{player.name} Adj.</td>
                    {results.adjusted[playerIndex].map((value, holeIndex) => (
                      <td key={holeIndex}>{value}</td>
                    ))}
                  </tr>
                ))}

                <tr>
                  <td className="row-label">Hole Value</td>
                  {results.holeValues.map((value, index) => (
                    <td key={index} className="bold-cell">{value || ""}</td>
                  ))}
                </tr>

                {players.map((player, playerIndex) => (
                  <tr key={`win-${playerIndex}`}>
                    <td className="row-label">{player.name} Won</td>
                    {results.winnerValues[playerIndex].map((value, holeIndex) => (
                      <td key={holeIndex}>
                        {value !== "" ? <span className="winner-pill">{value}</span> : ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bottom-grid">
          <div className="card">
            <h2>Totals</h2>
            <div className="totals-list">
              {players.map((player, index) => (
                <div className="totals-row" key={index}>
                  <div className="totals-player">{player.name}</div>
                  <div className="totals-values">
                    <div>
                      <span className="totals-label">Earned</span>
                      <strong>{formatNumber(results.totalEarned[index])}</strong>
                    </div>
                    <div>
                      <span className="totals-label">Profit/Loss</span>
                      <strong className={results.profitLoss[index] > 0 ? "positive" : results.profitLoss[index] < 0 ? "negative" : ""}>
                        {formatNumber(results.profitLoss[index])}
                      </strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card summary-card">
            <div>
              <div className="small-label">Total</div>
              <div className="big-number">{formatNumber(results.total)}</div>
            </div>
            <div>
              <div className="small-label">In Per Person</div>
              <div className="big-number">{formatNumber(results.perPerson)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;